import type { ReactElement, CSSProperties } from "react";

import type { StoryVersion } from "@/types/story";

/**
 * Badge size options
 */
type BadgeSize = "small" | "default" | "large";

/**
 * VersionBadge component props
 */
interface VersionBadgeProps {
  version?: StoryVersion;
  size?: BadgeSize;
  showLabel?: boolean;
  isProStory?: boolean;
}

/**
 * Version configuration
 */
interface VersionConfig {
  label: string;
  fullLabel: string;
  background: string;
  color: string;
  border?: string;
  description: string;
}

/**
 * Size style configuration
 */
interface SizeStyle {
  padding: string;
  fontSize: string;
  borderRadius: string;
}

const VERSION_CONFIG: Record<StoryVersion, VersionConfig> = {
  1: {
    label: "v1",
    fullLabel: "Standard",
    background: "#E8E8E8",
    color: "#6B7280",
    description: "Flux",
  },
  2: {
    label: "v2",
    fullLabel: "Premium",
    background: "linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)",
    color: "#3B82F6",
    description: "Gemini 2.5",
  },
  3: {
    label: "v3",
    fullLabel: "Pro",
    background: "linear-gradient(135deg, #FDF4FF 0%, #F3E8FF 100%)",
    color: "#7C3AED",
    border: "1px solid #DDD6FE",
    description: "Gemini 3 Pro",
  },
};

const SIZE_STYLES: Record<BadgeSize, SizeStyle> = {
  small: {
    padding: "1px 4px",
    fontSize: "9px",
    borderRadius: "4px",
  },
  default: {
    padding: "2px 6px",
    fontSize: "10px",
    borderRadius: "6px",
  },
  large: {
    padding: "4px 10px",
    fontSize: "12px",
    borderRadius: "8px",
  },
};

/**
 * VersionBadge Component
 *
 * Displays the story version based on image generation tier:
 * - v1 = Replicate Flux (free/credit users) - Gray/neutral
 * - v2 = Gemini 2.5 Flash (premium users) - Blue/teal
 * - v3 = Gemini 3 Pro (premium + pro story) - Purple/gold gradient
 */
const VersionBadge = ({
  version,
  size = "default",
  showLabel = false,
  isProStory = false,
}: VersionBadgeProps): ReactElement => {
  // Determine effective version: if is_pro_story but no version, it's a legacy pro story (v3)
  const effectiveVersion: StoryVersion = version || (isProStory ? 3 : 1);

  // Use effective version for config lookup
  const config = VERSION_CONFIG[effectiveVersion] || VERSION_CONFIG[1];
  const currentSize = SIZE_STYLES[size] || SIZE_STYLES.default;

  const style: CSSProperties = {
    fontFamily: "Quicksand, sans-serif",
    background: config.background,
    color: config.color,
    border: config.border || "none",
    ...currentSize,
  };

  return (
    <span
      className="font-bold flex items-center gap-0.5 flex-shrink-0"
      style={style}
      title={`${config.fullLabel} Quality (${config.description})`}
    >
      {config.label}
      {showLabel && (
        <span className="ml-0.5 opacity-80">{config.fullLabel}</span>
      )}
    </span>
  );
};

export default VersionBadge;
